/**
 * Premier League fantasy scoring.
 *
 * The rates document and every player performance share the same action keys,
 * so one list drives both: `performance[key]` is what the player did,
 * `points[key]` is what it was worth. Unused keys are 0 rather than absent.
 */

export const SCORING_ACTION_KEYS = [
  'appearance',
  'minutes_60',
  'goal_outside_box',
  'assist',
  'balls_recovered',
  'player_of_the_match',
  'penalty_won',
  'penalty_conceded',
  'penalty_missed',
  'yellow_card',
  'red_card',
  'own_goal',
  'goal_gk',
  'goal_def',
  'goal_mid',
  'goal_fwd',
  'penalty_save',
  'clean_sheet_gk',
  'clean_sheet_def',
  'clean_sheet_mid',
  'saves',
  'goals_conceded_gk',
  'goals_conceded_def',
] as const;

export type ScoringActionKey = (typeof SCORING_ACTION_KEYS)[number];

/** Every key present, zero where nothing happened. */
export type ScoringActionMap = Record<ScoringActionKey, number>;

/**
 * The rates the API scores by.
 *
 * Three keys are thresholds rather than rates: `balls_recovered_every`,
 * `saves_every` and `goals_conceded_every` say how many of that action earn
 * one unit of the matching rate.
 */
export interface ScoringRules extends ScoringActionMap {
  id: string;
  status: string;
  balls_recovered_every: number;
  saves_every: number;
  goals_conceded_every: number;
}

/** One player's match, scored. */
export interface PlayerPerformance {
  id: string;
  seriesId: number;
  fixtureId: number;
  playerId: number;
  teamId: number;
  opponentTeamId?: number;
  /** GK / DEF / MID / FWD, as the API stores it. */
  position?: string;
  wasHome?: boolean;
  playerName?: string;
  teamName?: string;
  teamShortCode?: string;
  playerImagePath?: string;
  teamImagePath?: string;
  performance: ScoringActionMap;
  points: ScoringActionMap;
  totalPoints: number;
  status?: string;
  scoredAt?: string;
  finalizedAt?: string;
}

/**
 * One of your eleven in a fixture.
 *
 * `totalPoints` is what the player scored; `appliedPoints` is what it was worth
 * to you, which is double for the captain. A player with no performance row yet
 * comes back with null detail and zero points rather than being left out.
 */
export interface TeamFixturePlayer {
  playerId: number;
  isCaptain: boolean;
  isViceCaptain: boolean;
  totalPoints: number;
  appliedPoints: number;
  performance: ScoringActionMap | null;
  points: ScoringActionMap | null;
  playerName?: string;
  teamName?: string;
  teamShortCode?: string;
  position?: string;
  playerImagePath?: string;
}

/** Your team's return from one fixture. */
export interface TeamFixturePoints {
  teamId: string;
  contestId: string;
  fixtureId: number;
  captainPlayerId: number;
  vicePlayerId: number;
  teamFixturePoints: number;
  /** Players the scoring run has not reached yet. */
  missingPlayerIds: number[];
  players: TeamFixturePlayer[];
}
